import { ChangeDetectionStrategy, Component, effect, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AssetStatus, GalleryDto } from '@nusign/picflow-backend-types';
import { SwitchComponent } from '../../../components/switch/switch.component';
import { debounce } from '../../../lib/debounce';
import { GalleryService } from '../../../services/gallery.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { RadioComponent } from '../../../shared-extra/components/radio/radio.component';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { getAssetStatusColor } from '../../pipes/asset-status-color.pipe';
import { getAssetStatusIcon } from '../../pipes/asset-status-icon.pipe';
import { AccessItemBodyComponent } from '../access-item-body/access-item-body.component';
import { AccessItemCtaComponent } from '../access-item-cta/access-item-cta.component';
import { AccessItemHeaderComponent } from '../access-item-header/access-item-header.component';
import { AccessItemComponent } from '../access-item/access-item.component';
import { GallerySettingsCustomizeHeaderComponent } from '../gallery-settings-customize-header/gallery-settings-customize-header.component';
import { GallerySettingsGroupComponent } from '../gallery-settings-group/gallery-settings-group.component';

@Component({
  selector: 'app-asset-status-setting-customize',
  imports: [
    GallerySettingsCustomizeHeaderComponent,
    GallerySettingsGroupComponent,
    AccessItemComponent,
    AccessItemHeaderComponent,
    AccessItemCtaComponent,
    SwitchComponent,
    FormsModule,
    AccessItemBodyComponent,
    InputFieldComponent,
    BillingScopePipe,
    RadioComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './asset-status-setting-customize.component.html',
  styleUrls: ['./asset-status-setting-customize.component.scss'],
})
export class AssetStatusSettingCustomizeComponent {
  galleryService = inject(GalleryService);
  gallery = input.required<GalleryDto>();
  back = output<void>();

  icons = {
    approved: getAssetStatusIcon('approved'),
    rejected: getAssetStatusIcon('rejected'),
    in_progress: getAssetStatusIcon('in_progress'),
    needs_review: getAssetStatusIcon('needs_review'),
  };

  iconColors = {
    approved: getAssetStatusColor('approved'),
    rejected: getAssetStatusColor('rejected'),
    in_progress: getAssetStatusColor('in_progress'),
    needs_review: getAssetStatusColor('needs_review'),
  };

  approved = model<boolean>();
  rejected = model<boolean>();
  in_progress = model<boolean>();
  needs_review = model<boolean>();

  limitActive = model<boolean>();
  limitValue = model<number>();
  softLimit = model<boolean>();
  forceLimitToggle = signal(false);

  constructor() {
    effect(() => {
      const gallery = this.gallery();
      if (!gallery) return;
      if (!gallery.enabled_feedback_options.includes('status')) {
        this.back.emit();
      }

      this.approved.set(gallery.enabled_asset_statuses.includes('approved'));
      this.rejected.set(gallery.enabled_asset_statuses.includes('rejected'));
      this.in_progress.set(gallery.enabled_asset_statuses.includes('in_progress'));
      this.needs_review.set(gallery.enabled_asset_statuses.includes('needs_review'));

      if (!this.forceLimitToggle()) {
        this.limitActive.set(!!gallery.approval_limits);
        this.limitValue.set(gallery.approval_limits ? Math.abs(gallery.approval_limits) : undefined);
        this.softLimit.set(gallery.approval_limits < 0);
      }
    });
  }

  updateAssetStatuses() {
    this.galleryService.updateGallery(this.gallery(), {
      enabled_asset_statuses: <AssetStatus[]>(
        [
          this.approved() ? 'approved' : undefined,
          this.rejected() ? 'rejected' : undefined,
          this.in_progress() ? 'in_progress' : undefined,
          this.needs_review() ? 'needs_review' : undefined,
          'no_status',
        ].filter(Boolean)
      ),
    });
  }

  updateApprovalLimit() {
    this.forceLimitToggle.set(true);
    if (!this.limitActive()) {
      this.galleryService.updateGallery(this.gallery(), {
        approval_limits: null,
      });
    } else if (this.limitValue()) {
      this.galleryService.updateGallery(this.gallery(), {
        approval_limits: this.softLimit() ? -this.limitValue() : this.limitValue(),
      });
    }
  }

  updateApprovalLimitDebounced = debounce(() => {
    this.updateApprovalLimit();
  }, 1000);
}
